// ============================================================================
// MarketOperatorPage — ОПЕРАТОРСКИЙ ЭКРАН маркетплейса.
//
// Три рабочие очереди на одном мобильном экране (одна колонка, без таблиц и
// окон платформы):
//   • «Мастера» — проверка профилей: причина прошлого отказа и документы,
//     одобрение/отклонение с причиной;
//   • «Возвраты» — оплаченные заявки: возврат стоимости взятия мастеру;
//   • «Архив» — заявки вне активных статусов (done/canceled/expired).
//
// Загрузка/ошибка/пусто — явные состояния. Живое обновление — события шины
// (data.catalog.mkt_masters.*, data.document.mkt_requests.*), без поллинга.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, RefreshCw, Search, ShieldCheck, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { useToastStore } from '@/store/toastStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { logErrorToJournal, logSwallowed } from '@/lib/errors';
import { toMarketFailure, type MarketFailure } from '@/lib/market';
import {
  isMarketArchivedRequest,
  isMarketRefundCandidate,
  loadMarketMasterDocuments,
  loadMarketMastersForReview,
  loadMarketOperatorRequests,
  refundMarketRequest,
  reviewMarketMaster,
  type MarketAttachment,
  type MarketMasterReview,
  type MarketOperatorRequest,
  type MasterReviewDecision,
} from '@/lib/marketOperator';
import { MarketEmpty, MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketMasterReviewCard } from '@/components/market/MarketMasterReviewCard';
import { MarketOperatorRequestCard } from '@/components/market/MarketOperatorRequestCard';
import { MarketOperatorDisputesTab } from '@/components/market/MarketOperatorDisputesTab';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { useMarketOperator } from '@/components/market/useMarketOperator';

type OperatorTab = 'masters' | 'disputes' | 'refunds' | 'archive';

interface MarketOperatorPageProps {
  onBack: () => void;
}

export function MarketOperatorPage({ onBack }: MarketOperatorPageProps) {
  const { t } = useI18n();
  const access = useMarketOperator();
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);

  const [tab, setTab] = useState<OperatorTab>('masters');
  const [query, setQuery] = useState('');
  const [tick, setTick] = useState(0);

  const [masters, setMasters] = useState<MarketMasterReview[] | null>(null);
  const [documents, setDocuments] = useState<Record<string, MarketAttachment[]>>({});
  const [mastersFailure, setMastersFailure] = useState<MarketFailure | null>(null);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [busyMaster, setBusyMaster] = useState('');

  const [requests, setRequests] = useState<MarketOperatorRequest[] | null>(null);
  const [requestsFailure, setRequestsFailure] = useState<MarketFailure | null>(null);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [busyRefund, setBusyRefund] = useState('');
  const [refunded, setRefunded] = useState<Record<string, boolean>>({});

  const loadMasters = useCallback(async () => {
    setMastersLoading(true);
    setMastersFailure(null);
    try {
      const list = await loadMarketMastersForReview();
      setMasters(list);
      const next: Record<string, MarketAttachment[]> = {};
      await Promise.all(list.map(async (master) => {
        try {
          next[master.id] = await loadMarketMasterDocuments(master.id);
        } catch (e) {
          // Документы — вспомогательные данные: сбой не скрывает очередь.
          logSwallowed(e, 'Оператор маркетплейса: документы мастера');
          next[master.id] = [];
        }
      }));
      setDocuments(next);
    } catch (e) {
      logErrorToJournal(e, 'Оператор маркетплейса: мастера на проверке');
      setMastersFailure(toMarketFailure(e));
    } finally {
      setMastersLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsFailure(null);
    try {
      setRequests(await loadMarketOperatorRequests());
    } catch (e) {
      logErrorToJournal(e, 'Оператор маркетплейса: заявки для возвратов и архива');
      setRequestsFailure(toMarketFailure(e));
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!access.isOperator) return;
    void loadMasters();
    void loadRequests();
  }, [access.isOperator, loadMasters, loadRequests, tick]);

  // Живое обновление очередей: решения по мастерам и изменения заявок.
  useEffect(() => {
    if (!access.isOperator) return;
    return eventBusHub.subscribe((ev) => {
      if (
        ev.topic.startsWith('data.catalog.mkt_masters.')
        || ev.topic.startsWith('data.document.mkt_requests.')
      ) {
        setTick((n) => n + 1);
      }
    });
  }, [access.isOperator]);

  const refundable = useMemo(
    () => (requests || []).filter(isMarketRefundCandidate),
    [requests],
  );
  const archived = useMemo(
    () => (requests || []).filter(isMarketArchivedRequest),
    [requests],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const visibleMasters = useMemo(() => {
    if (!normalizedQuery) return masters || [];
    return (masters || []).filter((m) => (
      [m.name, m.phone, m.email, m.about, m.reason]
        .some((value) => value.toLowerCase().includes(normalizedQuery))
    ));
  }, [masters, normalizedQuery]);

  const visibleRefunds = useMemo(() => {
    if (!normalizedQuery) return refundable;
    return refundable.filter((r) => (
      [r.number, r.descr, r.address, r.masterName, r.categoryName, r.statusLabel]
        .some((value) => String(value || '').toLowerCase().includes(normalizedQuery))
    ));
  }, [refundable, normalizedQuery]);
  const visibleArchive = useMemo(() => {
    if (!normalizedQuery) return archived;
    return archived.filter((r) => (
      [r.number, r.descr, r.address, r.masterName, r.categoryName, r.statusLabel]
        .some((value) => String(value || '').toLowerCase().includes(normalizedQuery))
    ));
  }, [archived, normalizedQuery]);

  const handleReview = async (
    master: MarketMasterReview,
    decision: MasterReviewDecision,
    reason: string,
  ) => {
    setBusyMaster(master.id);
    try {
      const result = await reviewMarketMaster(master.id, decision, reason, '');
      if (!result.ok) {
        showError(result.message);
        return;
      }
      showSuccess(
        result.already
          ? t('market.decision_saved')
          : decision === 'approve'
            ? t('market.master_confirmed')
            : t('market.master_rejected'),
      );
      setTick((n) => n + 1);
    } finally {
      setBusyMaster('');
    }
  };

  const handleRefund = async (
    request: MarketOperatorRequest,
    reason: string,
    comment: string,
  ): Promise<boolean> => {
    setBusyRefund(request.id);
    try {
      const result = await refundMarketRequest(request.id, reason, comment);
      if (!result.ok) {
        showError(result.message);
        return false;
      }
      setRefunded((prev) => ({ ...prev, [request.id]: true }));
      showSuccess(t('market.refund_done'));
      setTick((n) => n + 1);
      return true;
    } finally {
      setBusyRefund('');
    }
  };

  const tabs = [
    { id: 'masters', label: `Мастера${masters ? ` · ${masters.length}` : ''}` },
    { id: 'disputes', label: 'Споры' },
    { id: 'refunds', label: `Возвраты${requests ? ` · ${refundable.length}` : ''}` },
    { id: 'archive', label: `Архив${requests ? ` · ${archived.length}` : ''}` },
  ];

  if (!access.isOperator) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.operator')} onBack={onBack} />
        <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto p-4 sm:p-6">
          <MarketEmpty
            icon={<ShieldCheck className="h-5 w-5" />}
            title={t('market.no_access')}
            text={t('market.operator_denied_text')}
          />
        </div>
      </div>
    );
  }

  const searchPlaceholder = tab === 'masters'
    ? t('market.search_review')
    : tab === 'disputes'
      ? 'Поиск спора: номер, описание, адрес, мастер'
      : t('market.search_archive');

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={t('market.operator')}
        subtitle={t('market.operator_subtitle')}
        onBack={onBack}
        action={(
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 rounded-lg p-0"
            onClick={() => setTick((n) => n + 1)}
            disabled={mastersLoading || requestsLoading}
            aria-label={t('common.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${mastersLoading || requestsLoading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-3 p-4 pb-24 sm:p-6">
          <TabBar
            tabs={tabs}
            active={tab}
            group="market-operator"
            onChange={(id) => {
              setTab(id as OperatorTab);
              setQuery('');
            }}
          />

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-11 w-full rounded-xl border bg-background pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {tab === 'masters' && (
            mastersFailure ? (
              <MarketError failure={mastersFailure} onRetry={() => setTick((n) => n + 1)} />
            ) : mastersLoading && masters === null ? (
              <MarketLoading text={t('market.masters_loading')} />
            ) : visibleMasters.length === 0 ? (
              <MarketEmpty
                icon={<ShieldCheck className="h-5 w-5" />}
                title={masters && masters.length > 0 ? t('nav.nothing_found') : t('market.review_queue_empty')}
                text={masters && masters.length > 0
                  ? t('market.review_edit_hint')
                  : t('market.review_empty')}
              />
            ) : (
              <div className="space-y-2.5">
                {visibleMasters.map((master) => (
                  <MarketMasterReviewCard
                    key={master.id}
                    master={master}
                    documents={documents[master.id] || []}
                    busy={busyMaster === master.id}
                    onReview={(item, decision, reason) => void handleReview(item, decision, reason)}
                  />
                ))}
              </div>
            )
          )}

          {tab === 'disputes' && <MarketOperatorDisputesTab query={query} />}

          {tab === 'refunds' && (
            requestsFailure ? (
              <MarketError failure={requestsFailure} onRetry={() => setTick((n) => n + 1)} />
            ) : requestsLoading && requests === null ? (
              <MarketLoading text={t('market.paid_loading')} />
            ) : visibleRefunds.length === 0 ? (
              <MarketEmpty
                icon={<Undo2 className="h-5 w-5" />}
                title={refundable.length > 0 ? t('nav.nothing_found') : t('market.no_refunds')}
                text={refundable.length > 0
                  ? 'Измените запрос — поиск идёт по номеру, описанию, адресу и мастеру.'
                  : 'Здесь появятся заявки, по которым мастер оплатил взятие. Возврат выполняется с причиной и комментарием.'}
              />
            ) : (
              <div className="space-y-2.5">
                {visibleRefunds.map((request) => (
                  <MarketOperatorRequestCard
                    key={request.id}
                    request={request}
                    mode="refund"
                    busy={busyRefund === request.id}
                    refunded={Boolean(refunded[request.id])}
                    onRefund={handleRefund}
                  />
                ))}
              </div>
            )
          )}

          {tab === 'archive' && (
            requestsFailure ? (
              <MarketError failure={requestsFailure} onRetry={() => setTick((n) => n + 1)} />
            ) : requestsLoading && requests === null ? (
              <MarketLoading text={t('market.archive_loading')} />
            ) : visibleArchive.length === 0 ? (
              <MarketEmpty
                icon={<Archive className="h-5 w-5" />}
                title={archived.length > 0 ? t('nav.nothing_found') : t('market.archive_empty')}
                text={archived.length > 0
                  ? 'Измените запрос — поиск идёт по номеру, описанию, адресу и мастеру.'
                  : 'Сюда попадают выполненные, отменённые и просроченные заявки. История сохраняется.'}
              />
            ) : (
              <div className="space-y-2.5">
                {visibleArchive.map((request) => (
                  <MarketOperatorRequestCard
                    key={request.id}
                    request={request}
                    mode="archive"
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
